import {RunInAction} from "mobx-graphlink";
import {O} from "web-vcore";

/** Info about the most recent `Invalidate3JS()` call — the "why are we rendering?" diagnostic. */
export type Invalidate3JSInfo = {
	/** A short tag for the subsystem that requested the frame, e.g. `"terrain.interval"`, `"scenery.rebuild"`, `"building.mount"`, `"mapControls.zoom"`. */
	source: string;
	/** A human-readable reason, e.g. "chunks were processed since last render". */
	reason: string;
	/** `performance.now()` when the invalidate was requested. */
	at: number;
	/** The stack trace captured when the invalidate was requested (first N frames). */
	stack: string;
	/** How many times this exact (source, reason) pair has fired. */
	count: number;
};

class Invalidate3JSStore {
	/** Info for the most recent `Invalidate3JS()` call. Null until the first call. */
	@O accessor lastInvalidate: Invalidate3JSInfo | null = null;

	/** Monotonic call counter for all `Invalidate3JS()` calls (for detecting "is it still churning?"). */
	@O accessor totalCalls: number = 0;

	/** A per-source histogram of call counts, for spotting which subsystem keeps requesting frames. */
	@O.ref accessor callsBySource = new Map<string, number>();
}

export const invalidate3JSStats = new Invalidate3JSStore();

/** The number of stack frames captured on each invalidate (kept small to avoid allocation churn). */
const STACK_DEPTH = 8;
/** The max number of frames held in the recorded stack string. */
const STACK_FRAME_LIMIT = 5;

/**
 * The single sanctioned way to request a three.js frame from outside R3F's own render loop.
 *
 * In on-demand (`frameloop="demand"`) mode, R3F only re-renders when `invalidate()` is called.
 * Every call site in the codebase should route through this wrapper so the Stats panel can show
 * *why* the map keeps rendering (or, when it should be idle, which subsystem is churning).
 *
 * @param invalidate The R3F `invalidate` function (from `useThree().invalidate`).
 * @param source A short stable tag naming the subsystem that requested the frame.
 * @param reason A human-readable description of why.
 */
export function Invalidate3JS(invalidate: (()=>void) | null, source: string, reason: string): void {
	const stack = new Error().stack?.split("\n").slice(2, 2 + STACK_DEPTH).slice(0, STACK_FRAME_LIMIT).join("\n") ?? "";
	RunInAction("Invalidate3JS", ()=>{
		const last = invalidate3JSStats.lastInvalidate;
		const samePair = last != null && last.source == source && last.reason == reason;
		const count = samePair ? last.count + 1 : 1;
		invalidate3JSStats.lastInvalidate = {source, reason, at: performance.now(), stack, count};
		invalidate3JSStats.totalCalls++;
		invalidate3JSStats.callsBySource.set(source, (invalidate3JSStats.callsBySource.get(source) ?? 0) + 1);
	});
	invalidate?.();
}
